/**
 * Zero-price cart sweep (08.09.2026) — loaded globally from layout/theme.liquid,
 * next to consultation-cart-guard.js, on the shared `#cart-line-sweep` module.
 *
 * Sage articles without a Shop-Preis arrive from the IntelliCon feed as price
 * 0,00 (no compare-at). The theme offers no add-to-cart for such a variant
 * (PDP, sticky bar, quick view; products that price 0 throughout never render a
 * card), so a 0,00 line can only reach the cart through a hand-built request
 * (`GET /cart/add?id=…`, `/cart/add.js`, a shared cart link) — or from a cart
 * that was filled before the rule existed. Shopify runs a 0,00 checkout through
 * without payment, so this guard removes every such line server-side
 * (`/cart/change`, quantity 0) as soon as the cart UI shows one, and tells the
 * customer why.
 *
 * Scope, honestly stated: client-side only. A checkout permalink
 * (`/cart/<variant>:<qty>`) or a script that never loads a theme page still
 * reaches checkout; that is the job of the server-side Cart & Checkout
 * Validation (Function Studio rule "Nullpreis: Checkout-Sperre"), not of this
 * file.
 *
 * Signal: the drawer (snippets/cart-drawer-item.liquid) and the cart page
 * (sections/main-cart-items.liquid) render `[data-zero-price-line="<key>"]` on
 * the badge of each unpriced line. Liquid decides what is unpriced — variant
 * price 0, no bundle components, product not allowed to price 0 (see
 * snippets/product-allows-zero-price.liquid) — this file only reads keys.
 *
 * Entry point only — nothing imports this file; the exports exist for
 * tests/zero-price-cart-guard.test.mjs. Side effects live in boot().
 */

import { collectMarkedLines, bootLineGuard } from '#cart-line-sweep';

export const SOURCE = 'zero-price-cart-guard';
export const LINE_ATTR = 'data-zero-price-line';
export const TITLE_ATTR = 'data-zero-price-title';
export const NOTICE_ATTR = 'data-zero-price-removed';

/** Reads the server-rendered markers. Pure, deduplicated by line key. */
export function collectUnpricedLines(doc) {
  return collectMarkedLines(doc, { attr: LINE_ATTR, titleAttr: TITLE_ATTR });
}

function boot() {
  bootLineGuard({
    source: SOURCE,
    attr: LINE_ATTR,
    titleAttr: TITLE_ATTR,
    noticeAttr: NOTICE_ATTR,
    noticeClass: 'cart-drawer__consultation-notice cart-drawer__zero-price-notice',
    noticeTemplate: () => window.cartStrings?.zeroPriceRemoved,
  });
}

if (typeof document !== 'undefined') boot();
